import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Box, Checkbox, DialogActions, DialogContent, FormControlLabel, Typography } from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import SharedButton from '../../shared/components/Button'
import SharedDialog from '../../shared/components/Dialog'
import InputWithValidation from '../../shared/components/InputWithValidation'
import { useThemeMode } from '../../shared/hooks'
import { getDialogTextFieldSx } from '../../shared/styles/formStyles'
import { notify } from '../../shared/utilities/util'
import { useConfirmA2p10dlcMutation, useWithdrawA2p10dlcMutation } from '../../store/api/adminApi'
import {
  A2P_RECORD_COPY,
  A2P_WITHDRAW_COPY,
  a2pConfirmBody,
  a2pWithdrawBody,
  canRecordA2p,
  canWithdrawA2p,
  runDialogSubmit,
  validateA2pNote,
  validateWithdrawReason,
} from './eSignatureModel'

/**
 * The A2P 10DLC readiness check's two dialogs on the subscriber E-signature tab (approved mockup v3):
 * Record a confirmation (a note plus an attestation), and Withdraw it (a reason). Each is mounted
 * only while open, so a reopened dialog always starts empty. A refusal - including a 409 (already
 * confirmed, nothing to withdraw, changed concurrently) - is shown inside the still-open dialog;
 * success closes it, and the overview refetches through the `SigningProvider` tag.
 */

/** A server refusal, in place inside a dialog (the interceptor toast is silenced for these calls). */
export const DialogError = ({ message }: { message: string }) => (
  <Box
    role="alert"
    sx={{
      mt: 2,
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1,
      px: 1.5,
      py: 1.25,
      borderRadius: '8px',
      bgcolor: '#fef2f2',
      border: '1px solid #fecaca',
    }}
  >
    <ErrorOutlineIcon sx={{ fontSize: 18, color: '#ef4444', flexShrink: 0, mt: '1px' }} />
    <Typography sx={{ fontSize: '13px', color: '#b91c1c', lineHeight: 1.5 }}>{message}</Typography>
  </Box>
)

interface A2pDialogProps {
  companyId: string
  onClose: () => void
}

export const A2pRecordDialog = ({ companyId, companyName, onClose }: A2pDialogProps & { companyName: string }) => {
  const { isDark } = useThemeMode()
  const [confirmA2p, { isLoading: saving }] = useConfirmA2p10dlcMutation()
  const [attested, setAttested] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<{ note: string }>({ defaultValues: { note: '' } })
  const note = watch('note') ?? ''

  const close = () => {
    if (!saving) onClose()
  }

  const onConfirm = handleSubmit(async ({ note: typed }) => {
    setSubmitError(null)
    const failure = await runDialogSubmit(
      () => confirmA2p(a2pConfirmBody(companyId, typed)).unwrap(),
      A2P_RECORD_COPY.fallbackError
    )
    if (failure) {
      setSubmitError(failure)
      return
    }
    notify(A2P_RECORD_COPY.success, 'success')
    onClose()
  })

  return (
    <SharedDialog variant="form" open onClose={close} title={A2P_RECORD_COPY.title} actions={null} maxWidth={520}>
      <Box component="form" onSubmit={(event: React.FormEvent) => void onConfirm(event)} noValidate>
        <DialogContent sx={{ pt: 1, pb: 1 }}>
          <Typography sx={{ fontSize: '14px', color: 'text.primary', mb: 2 }}>
            The server cannot check A2P 10DLC itself. Record that you confirmed it for{' '}
            <strong>{companyName || 'this company'}</strong>.
          </Typography>
          <InputWithValidation
            name="note"
            control={control}
            errors={errors}
            rules={{ validate: validateA2pNote }}
            label={A2P_RECORD_COPY.noteLabel}
            placeholder={A2P_RECORD_COPY.notePlaceholder}
            autoFocus
            sx={getDialogTextFieldSx(isDark)}
          />
          <FormControlLabel
            sx={{
              mt: 1.5,
              alignItems: 'flex-start',
              '& .MuiFormControlLabel-label': { fontSize: '14px', pt: 0.75 },
            }}
            control={<Checkbox checked={attested} onChange={(event) => setAttested(event.target.checked)} />}
            label={A2P_RECORD_COPY.attestation}
          />
          {submitError && <DialogError message={submitError} />}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1.5 }}>
          <SharedButton label="Cancel" btnVariant="secondary" fullWidth onClick={close} disabled={saving} />
          <SharedButton
            label={A2P_RECORD_COPY.confirmLabel}
            fullWidth
            type="submit"
            loading={saving}
            disabled={!canRecordA2p(note, attested)}
          />
        </DialogActions>
      </Box>
    </SharedDialog>
  )
}

export const A2pWithdrawDialog = ({ companyId, onClose }: A2pDialogProps) => {
  const { isDark } = useThemeMode()
  const [withdrawA2p, { isLoading: saving }] = useWithdrawA2p10dlcMutation()
  const [submitError, setSubmitError] = useState<string | null>(null)
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<{ reason: string }>({ defaultValues: { reason: '' } })
  const reason = watch('reason') ?? ''

  const close = () => {
    if (!saving) onClose()
  }

  const onConfirm = handleSubmit(async ({ reason: typed }) => {
    setSubmitError(null)
    const failure = await runDialogSubmit(
      () => withdrawA2p(a2pWithdrawBody(companyId, typed)).unwrap(),
      A2P_WITHDRAW_COPY.fallbackError
    )
    if (failure) {
      setSubmitError(failure)
      return
    }
    notify(A2P_WITHDRAW_COPY.success, 'success')
    onClose()
  })

  return (
    <SharedDialog variant="form" open onClose={close} title={A2P_WITHDRAW_COPY.title} actions={null} maxWidth={520}>
      <Box component="form" onSubmit={(event: React.FormEvent) => void onConfirm(event)} noValidate>
        <DialogContent sx={{ pt: 1, pb: 1 }}>
          <Box component="ul" sx={{ m: 0, mb: 2, pl: 2.5 }}>
            {A2P_WITHDRAW_COPY.bullets.map((bullet) => (
              <Typography component="li" key={bullet} sx={{ fontSize: '14px', color: 'text.primary', my: 0.5 }}>
                {bullet}
              </Typography>
            ))}
          </Box>
          <InputWithValidation
            name="reason"
            control={control}
            errors={errors}
            rules={{ validate: validateWithdrawReason }}
            label={A2P_WITHDRAW_COPY.reasonLabel}
            placeholder={A2P_WITHDRAW_COPY.reasonPlaceholder}
            autoFocus
            sx={getDialogTextFieldSx(isDark)}
          />
          {submitError && <DialogError message={submitError} />}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1.5 }}>
          <SharedButton label="Cancel" btnVariant="secondary" fullWidth onClick={close} disabled={saving} />
          <SharedButton
            label={A2P_WITHDRAW_COPY.confirmLabel}
            btnVariant="danger"
            fullWidth
            type="submit"
            loading={saving}
            disabled={!canWithdrawA2p(reason)}
          />
        </DialogActions>
      </Box>
    </SharedDialog>
  )
}
